import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";

export default function Trust() {
  const [expandedSection, setExpandedSection] = useState(null);
  const { t } = useI18n();

  const sections = [
    {
      id: "tenants",
      title: "Trust for Tenants",
      subtitle: "Your deposit is yours.",
      points: [
        "Deposit held securely by Orello — not in the landlord's account",
        "Room-by-room move-in condition report, timestamped and locked",
        "You review and approve every deduction — nothing moves without your sign-off",
        "Dispute anything: ORE-AI reviews all evidence and gives an instant verdict",
        "If still unresolved: manual Orello review within 8 days — final and binding",
      ],
    },
    {
      id: "landlords",
      title: "Trust for Landlords",
      subtitle: "Your property is protected.",
      points: [
        "Aadhaar-verified tenants — real KYC, not a forwarded ID photo",
        "E-stamped, digitally signed agreements valid under Indian law",
        "Room-by-room condition report with photos locked at move-in",
        "Your deduction claims are reviewed against documented evidence",
        "ORE-AI applies local market rates — fair to you, transparent to the tenant",
      ],
    },
    {
      id: "brokers",
      title: "Trust for Brokers",
      subtitle: "Your business is transparent.",
      points: [
        "Set and collect your brokerage transparently through the platform",
        "Disputes go to ORE-AI — you are never the middleman again",
        "All agreements digitally signed, e-stamped, and court-valid",
        "Full dashboard: every deal, every status, every report in one place",
        "Refer agents and earn referral bonuses on their closed deals",
      ],
    },
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero Section */}
      <PageHero
        eyebrow={t("nav_trust")}
        title={t("we_protect_everyone")}
        subtitle="From securely held deposits to court-valid agreements to ORE-AI powered dispute resolution — every layer of Orello is built to protect all parties."
        cta1Text="Get Started"
        cta1Link="/register"
        hasGlow={true}
        gradientWord="everyone"
      />

      {/* How We Build Trust */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="mb-16 text-center">
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                color: "#F5F5F5",
                marginBottom: "2rem",
              }}
            >
              How Trust Works on Orello
            </h2>
          </div>

          {/* Key Pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                label: "Deposit Held by Orello",
                desc: "Your deposit is held securely by Orello — not by the landlord, not by the broker. Released only on mutual agreement.",
              },
              {
                label: "Court-Valid Agreements",
                desc: "Every agreement is digitally signed and e-stamped under Indian rental law. Valid anywhere in India, downloadable anytime.",
              },
              {
                label: "ORE-AI Dispute Engine",
                desc: "India's first AI built for rental disputes. Analyses check-in photos, checkout reports, and local market rates — gives an instant, neutral, binding verdict.",
              },
            ].map((pillar, i) => (
              <div
                key={i}
                style={{
                  padding: "2rem",
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <h3
                  className="font-display font-semibold mb-3"
                  style={{ fontSize: "1.15rem", color: "#F5F5F5" }}
                >
                  {pillar.label}
                </h3>
                <p style={{ fontSize: "0.9rem", color: "rgba(245,245,245,0.5)", lineHeight: 1.6 }}>
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Collapsible Sections */}
          <div className="space-y-4">
            {sections.map((section) => (
              <div
                key={section.id}
                style={{
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === section.id ? null : section.id
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                  }}
                >
                  <div className="text-left">
                    <h3
                      className="font-display font-semibold"
                      style={{ fontSize: "1.25rem", color: "#F5F5F5" }}
                    >
                      {section.title}
                    </h3>
                    <p style={{ fontSize: "0.9rem", color: "rgba(245,245,245,0.4)", marginTop: "0.25rem" }}>
                      {section.subtitle}
                    </p>
                  </div>
                  <ChevronDown
                    size={24}
                    style={{
                      color: "#F97316",
                      transition: "transform 0.3s ease",
                      transform:
                        expandedSection === section.id ? "rotate(180deg)" : "rotate(0deg)",
                      flexShrink: 0,
                      marginLeft: "1rem",
                    }}
                  />
                </button>

                {/* Expanded content */}
                {expandedSection === section.id && (
                  <div
                    style={{
                      padding: "0 2rem 2rem 2rem",
                      borderTop: "1px solid rgba(245,245,245,0.06)",
                      background: "rgba(249,115,22,0.05)",
                    }}
                  >
                    <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                      {section.points.map((point, i) => (
                        <li
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "1rem",
                            paddingTop: i > 0 ? "1rem" : "0",
                            marginTop: i > 0 ? "1rem" : "0",
                            borderTop: i > 0 ? "1px solid rgba(245,245,245,0.06)" : "none",
                          }}
                        >
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              background: "#F97316",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginTop: "0.25rem",
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#080808",
                              }}
                            />
                          </div>
                          <p style={{ color: "rgba(245,245,245,0.6)", lineHeight: 1.6 }}>
                            {point}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory & Compliance */}
      <section style={{ background: "#111111", padding: "4rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
            <p
              className="text-xs tracking-widest uppercase"
              style={{
                background: "linear-gradient(135deg, #F97316, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Built on Compliance
            </p>
          </div>
          <h2
            className="font-display font-bold mb-6"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.5rem)",
              color: "#F5F5F5",
            }}
          >
            Regulated. Legal. Safe.
          </h2>
          <p
            className="max-w-2xl mx-auto text-base font-light mb-12"
            style={{ color: "rgba(245,245,245,0.4)", lineHeight: 1.8 }}
          >
            Orello operates under Indian rental law. Every agreement is e-stamped and digitally signed. Every deposit is held securely until both parties agree. Every dispute is decided by ORE-AI — India's first purpose-built rental dispute engine — using documented evidence, photos, and local market rates. No bias. No shortcuts. No WhatsApp arguments.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "#080808", padding: "4rem 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-display font-bold leading-tight mb-6"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Join thousands who trust Orello.<br />
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EC4899)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              with their deposits.
            </span>
          </h2>
          <p className="text-base font-light mb-12" style={{ color: "rgba(245,245,245,0.35)" }}>
            No credit card. No setup fee. Ready in minutes.
          </p>
          <Link
            to="/register"
            className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              fontSize: "1.05rem",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
            }}
          >
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
